import type React from 'react'
import { Calendar, Trash2 } from 'lucide-react'
import { ClarifyEntriesIcon } from '@/src/renderer/components/icons/ClarifyEntriesIcon'
import { useTranslation } from 'react-i18next'
import { TodayIcon, TomorrowIcon } from '@/src/renderer/features/taskManagement/components/WhenDropdown'
import { DormantFilledIcon } from '@/src/renderer/components/icons/DormantIcon'
import { ProjectIcon } from '@/src/renderer/components/common/ProjectIcon'
import { Dot } from '@/src/renderer/components/ui/Dot'
import type { ClarifyEntryCardProps } from './ClarifyEntryCard.types'
import type { StagedAction } from './useClarifyState'
import { formatRelativeAge, formatShortDate } from './clarifyFormatters'
import './ClarifyEntryCard.scss'

// Match the lucide icon API (size + optional className) so the shared meta
// icon renderer below can treat the filled Figma Today/Tomorrow icons
// interchangeably with the remaining lucide glyphs.
const TodayActionIcon: React.FC<{ size?: number; className?: string }> = ({
  size,
  className,
}) => (
  <span className={className}>
    <TodayIcon size={size} />
  </span>
)

const TomorrowActionIcon: React.FC<{ size?: number; className?: string }> = ({
  size,
  className,
}) => (
  <span className={className}>
    <TomorrowIcon size={size} />
  </span>
)

const ACTION_ICONS = {
  today: TodayActionIcon,
  tomorrow: TomorrowActionIcon,
  later: Calendar,
  pause: DormantFilledIcon,
  // Wie „Verschieben …“ im Panel.
  move: ClarifyEntriesIcon,
  newProject: ClarifyEntriesIcon,
  delete: Trash2,
} as const

/**
 * Card representation of one inbox entry in the Clarify-Mode left column.
 *
 * Four visual states:
 *  - `active`          — weiß mit violettem Border, zentraler Focus
 *  - `staged-inline`   — grauer BG, volle Höhe, Action-Icon inline statt start-date badge
 *  - `staged-in-stack` — kompakt (~2/3 Höhe), reduzierter Inhalt, für Stack-Overlay
 *  - `upcoming`        — weiß ohne Border, für noch nicht-bearbeitete Cards
 */
export function ClarifyEntryCard({
  task,
  variant,
  stagedAction,
  onClick,
  stackDepth = 0,
}: ClarifyEntryCardProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'en' ? 'en' : 'de'

  const meta = buildMeta(stagedAction, task.createdAt, locale, t)

  const classes = [
    'clarify-entry-card',
    `clarify-entry-card--${variant}`,
    stagedAction?.kind === 'delete' ? 'clarify-entry-card--destructive' : null,
    stagedAction?.kind === 'later' ? 'clarify-entry-card--later' : null,
    stagedAction?.kind === 'move' || stagedAction?.kind === 'newProject' ? 'clarify-entry-card--move' : null,
    stagedAction ? 'clarify-entry-card--has-staged-action' : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      className={classes}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!onClick) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      data-stack-depth={variant === 'staged-in-stack' ? stackDepth : undefined}
    >
      {/* Das farbige Aktions-Symbol steht vor dem Titel (Sven, 06.10.2026), nicht mehr in der Zeile darunter. */}
      <div className="clarify-entry-card__title-row">
        {variant !== 'staged-in-stack' && meta?.icon && (
          <meta.icon size={14} className="clarify-entry-card__action-icon" />
        )}
        <div className="clarify-entry-card__title">{task.title}</div>
      </div>
      {variant !== 'staged-in-stack' && meta && (
        <div className="clarify-entry-card__meta">
          {meta.target ? (
            // „Nach [Icon] Onboarding Tom“ (Sven, 06.10.2026); gekürzt wird nur der Name.
            <span className="clarify-entry-card__meta-primary clarify-entry-card__meta-target">
              {t('quickNotes.clarify.moveTo')}
              <span className="clarify-entry-card__meta-project-icon">
                <ProjectIcon icon={meta.target.icon} iconColor={meta.target.iconColor} size={14} />
              </span>
              <span className="clarify-entry-card__meta-target-name">{meta.target.name}</span>
            </span>
          ) : meta.primary && <span className="clarify-entry-card__meta-primary">{meta.primary}</span>}
          {(meta.primary || meta.target) && meta.space && <Dot className="clarify-entry-card__meta-dot" />}
          {meta.space && <span className="clarify-entry-card__meta-space">{meta.space}</span>}
          {(meta.primary || meta.space) && meta.secondary && <Dot className="clarify-entry-card__meta-dot" />}
          {meta.secondary && <span className="clarify-entry-card__meta-secondary">{meta.secondary}</span>}
        </div>
      )}
    </div>
  )
}

type MetaInfo = {
  icon: React.ComponentType<{ size?: number; className?: string }> | null
  primary: string | null
  /** Das Ziel beim Verschieben — mit dem Icon seines Projekts. */
  target?: { name: string; icon: string | null; iconColor: string | null }
  /** Der Lebensbereich des Ziels beim Verschieben — es kann in einem anderen liegen. */
  space?: string
  secondary: string | null
}

function buildMeta(
  staged: StagedAction | undefined,
  createdAt: Date,
  locale: 'de' | 'en',
  t: ReturnType<typeof useTranslation>['t'],
): MetaInfo | null {
  const age = formatRelativeAge(createdAt, locale)
  if (!staged) {
    return { icon: null, primary: null, secondary: age }
  }
  const Icon = ACTION_ICONS[staged.kind]
  if (staged.kind === 'later') {
    return { icon: Icon, primary: formatShortDate(staged.date, locale), secondary: age }
  }
  // „Nach Onboarding Tom · Arbeit“ (Sven, 06.10.2026): was passiert und wohin — ohne das Alter, sonst
  // kürzte sich das Ziel weg.
  if (staged.kind === 'move') {
    const target = { name: staged.place, icon: staged.icon ?? null, iconColor: staged.iconColor ?? null }
    return { icon: Icon, primary: null, target, space: t(`lifeSpaces.${staged.lifeSpace}`), secondary: null }
  }
  // Angelegt wird erst beim Buchen (#267) — „Nach [Standard-Icon] Umzug (neu)“, wie es entstehen wird.
  if (staged.kind === 'newProject') {
    const target = { name: t('quickNotes.clarify.newProjectPlace', { name: staged.name }), icon: null, iconColor: null }
    return { icon: Icon, primary: null, target, space: t(`lifeSpaces.${staged.lifeSpace}`), secondary: null }
  }
  return { icon: Icon, primary: null, secondary: age }
}
